import { CSSResultGroup, html, LitElement, PropertyValues, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { style } from './option.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'


initializeIconsByRole({
  'cse-option.check': { name: 'check', style: 'regular' },
  'cse-option.arrow': { name: 'angle-right', style: 'regular' },
})

export const optionVariants = ['select', 'search'] as const
export type optionVariantsType = typeof optionVariants

@customElement('cse-option')
export class CSEOption extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style, fontStyles]

  @property({ reflect: true })
  value = ''

  @property({ type: Boolean, reflect: true })
  selected = false

  @property({ type: Boolean, reflect: true })
  disabled = false

  @property({ type: String })
  variant: optionVariantsType[number] = 'select'

  constructor() {
    super()
    this.addEventListener('click', this.handleOptionClick)
    this.addEventListener('keydown', this.handleKeyDown)
  }

  override focus() {
    const wrapper = this.shadowRoot?.querySelector('.cse-option') as HTMLElement
    wrapper?.focus()
  }

  get labelText(): string {
    if (this.textContent !== null) {
      return this.textContent.trim()
    }
    throw new Error('no label text found for cse-option')
  }

  private handleSelectedChange() {
    this.setAttribute('aria-selected', this.selected ? 'true' : 'false')
  }

  private handleDisabledChange() {
    this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false')
  }

  private handleOptionClick = () => {
    if (this.disabled) { return }
    this.dispatchEvent(new CSEOptionClickedEvent(this))
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    if (this.disabled) return

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      this.dispatchEvent(new CSEOptionClickedEvent(this))
    }
  }

  private renderSuffix = () => html`
    <slot name="suffix" class="icon">
      ${this.selected ? html`
        <cse-icon icon-role="cse-option.check"></cse-icon>
      ` : ''}
      ${this.variant==='search' ? html`
        <cse-icon icon-role="cse-option.arrow"></cse-icon>
      ` : ''}
    </slot>
  `

  override render(): TemplateResult {
    const classes: { [className: string]: boolean } = {
      'cse-option': true,
      'selected': this.selected && this.variant!=='search',
      'disabled': this.disabled,
      [`variant-${this.variant}`]: true
    }

    return html`
      <div class="${classMap(classes)}" tabindex="0">
        <slot name="prefix" class="icon"></slot>
        <slot id="label-text" class="font-body"></slot>
        ${this.renderSuffix()}
      </div>
    `
  }

  protected override update(changedProperties: PropertyValues<this>) {
    super.update(changedProperties)
    if (changedProperties.has('disabled')) { this.handleDisabledChange() }
    if (changedProperties.has('selected')) { this.handleSelectedChange() }
  }
}

export class CSEOptionClickedEvent extends Event {
  static type = 'cse-option-clicked' as const

  option: CSEOption

  constructor(option: CSEOption) {
    super(CSEOptionClickedEvent.type, { bubbles: true, composed: true })
    this.option = option
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-option': CSEOption
  }
  interface GlobalEventHandlersEventMap {
    [CSEOptionClickedEvent.type]: CSEOptionClickedEvent,
  }
}
